import { useQuery } from '@tanstack/react-query';

import {
  OTA_EXECUTION_STATUS,
  OtaExecutionStatus,
  OtaStepStatus,
} from '../types';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';

export interface IOtaStep {
  step: string;
  status: OtaStepStatus;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
}

interface IGetActiveOtaResponse {
  execution: {
    status: OtaExecutionStatus;
    steps: IOtaStep[];
  };
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const errorWithStatus = error as {
    status?: number;
    response?: { status?: number };
  };

  return errorWithStatus.status ?? errorWithStatus.response?.status;
};

export const fetchActiveOta = async ({
  makeRequest,
  lpUI,
}: {
  makeRequest: ReturnType<typeof useImperativeRequestWrapper>[1];
  lpUI: string;
}): Promise<IGetActiveOtaResponse | null> => {
  const { data, error } = await makeRequest({
    path: `/products/${lpUI}/ota/executions/active`,
    method: 'get',
  });

  // 404 means no active OTA execution for this box.
  if (error && getErrorStatus(error) === 404) {
    return null;
  }

  if (!data || error) {
    throw new Error(error?.response?.data?.message ?? error);
  }

  return data;
};

type QueryFnType = typeof fetchActiveOta;

export const computeRefetchInterval = (
  data: IGetActiveOtaResponse | null | undefined
): number | false =>
  data?.execution?.status === OTA_EXECUTION_STATUS.IN_PROGRESS ? 10_000 : false;

type GetActiveOtaOptions = {
  lpUI: string;
  enabled?: boolean;
  config?: QueryConfig<QueryFnType>;
};

// Reason: Bulletproof React convention — query hooks are named getXxx (no use prefix).
// eslint-disable-next-line react-hooks/rules-of-hooks
export const getActiveOta = ({
  lpUI,
  enabled = true,
  config,
}: GetActiveOtaOptions) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [, makeRequest] = useImperativeRequestWrapper('deviceV3');

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    queryKey: ['ota', 'active', lpUI],
    queryFn: () => fetchActiveOta({ makeRequest, lpUI }),
    enabled,
    staleTime: 0,
    refetchInterval: (query) =>
      computeRefetchInterval(
        query.state.data as IGetActiveOtaResponse | null | undefined
      ),
  });
};
